import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ListingTable, {
    ListingTableColumn,
} from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { useToasts } from 'react-toast-notifications';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { Paged } from 'api/common/types/Page';
import styles from './AdditionalDataList.module.scss';
import InputSearch from 'common/components/inputSearch/InputSearch';
import { useDebouncedCallback } from 'use-debounce';
import DropdownItem from 'common/components/popover/PopoverItem';
import Dropdown from 'common/components/dropdown/Dropdown';
import { FaEllipsisH } from 'react-icons/fa';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { useHistory } from 'react-router';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import { AdditionalDataListItemDto } from '../../../api/additional-data/models/AdditionalDataListItemDto';
import { AdditionalDataSearchCriteria } from '../../../api/additional-data/models/AdditionalDataSearchCriteria';
import AdditionalDataService from '../../../api/additional-data/AdditionalDataService';

type Props = {    
    companyId: string;
};
const AdditionalDataList = ({ companyId }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const [additionalDataPage, setAdditionalDataPage] = useState<Paged<AdditionalDataListItemDto>>();
    const [criteria, setCriteria] = useState<AdditionalDataSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderBy: 'asc',
        orderColumn: 'adf.designation',
        companyId: companyId,
        connectionTranslations: t('additional_data.link', { returnObjects: true })
    });

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasAdditionalDataWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_COMPANIES_WRITE'], 'OR');
    const history = useHistory();
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<AdditionalDataListItemDto | null>(null);
    const showRemoveItemDialog = async (item: AdditionalDataListItemDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onSelectAdditionalData = (item: AdditionalDataListItemDto | null, details: boolean) => {
        if (!item) {
            history.push(`/companies/details/${companyId}/additionaldata/new`);
            return;
        }
        history.push(`/companies/details/${companyId}/additionaldata/${details ? 'details' : 'edit'}/${item.id}`);
        return;
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            await AdditionalDataService.remove({ ...itemToRemove,companyId });
            setCriteria({ ...criteria, page: 1 });
            onCancelRemove();
            addToast(t('common.messages.record_delete_success'), {
                appearance: 'success',
            });
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete additional data`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };

    const renderTableActionCell = (row: AdditionalDataListItemDto) => {
        return (
            <Dropdown
                options={
                    <>
                        <DropdownItem onClick={() => onSelectAdditionalData(row, true)}>
                            {t('common.details')}
                        </DropdownItem>
                        { hasAdditionalDataWritePolicy && <DropdownItem onClick={() => onSelectAdditionalData(row, false)}>
                            {t('common.edit')}
                        </DropdownItem>}
                        {hasAdditionalDataWritePolicy && <DropdownItem onClick={() => showRemoveItemDialog(row)}>
                            {t('common.remove')}
                        </DropdownItem>}
                    </>
                }
            >
                <div>
                    <FaEllipsisH />
                </div>
            </Dropdown>
        );
    };

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const tableColumns: ListingTableColumn<AdditionalDataListItemDto>[] =
        useMemo(() => {
            const cols: ListingTableColumn<AdditionalDataListItemDto>[] = [
                {
                    field: 'designation',
                    name: t('additional_data.list.designation'),
                    onSearch: onTableFilter,
                    searchField: 'adf.designation'
                },
                {
                    field: 'link',
                    name: t('additional_data.list.link'),
                    onSearch: onTableFilter,
                    renderCell: row => <>{t(`additional_data.link.${row.link}` as any)}</>,
                    searchField: 'translated_link_name'
                },
                {
                    field: 'type',
                    name: t('additional_data.list.type'),
                    onSearch: onTableFilter,
                    renderCell: row => <>{t(`additional_data.type.${row.type}` as any)}</>,
                    searchField: 'ad.type'
                },
                 {
                    field: 'order',
                    name: t('additional_data.list.order'),
                    onSearch: onTableFilter,
                    renderCell: row => <>{row.order}</>,
                    searchField: 'ad.order'
                },
                {
                    field: 'showInListing',
                    name: t('additional_data.show_in_listing'),
                    renderCell: row => <>{row.showInListing ? t('common.yes') : t('common.no')}</>,
                },
                {
                    name: '',
                    width: '60px',
                    cellAlignment: 'right',
                    preventClick: true,
                    renderCell: renderTableActionCell,
                    cellStyle: { overflow: 'unset' }
                },
            ];
            return cols;
        }, []);

    const getData = async () => {
        try {
            Loading.show();
            const page = await AdditionalDataService.getList(criteria);
            setAdditionalDataPage(page);
            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't get additional data list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
        }
    };

    useEffect(() => {
        getData();
    }, [criteria]);

    const debounced = useDebouncedCallback((value: string) => {
        setCriteria({ ...criteria, page: 1, allIn: value });
    }, 500);

    return (
        <div className={`list ${styles.container}`}>
            <div className={styles.tabHeader}>
                <div>
                    <InputSearch onChangeValue={debounced} placeholder={t('additional_data.list.search_placeholder')} ></InputSearch>
                </div>
                <div>
                    {hasAdditionalDataWritePolicy && <ScreenHeaderButton icon={AddIcon} onClick={() => onSelectAdditionalData(null, false)} />}
                </div>
            </div>
            <ListingTable
                columns={tableColumns}
                rows={additionalDataPage?.items || []}
                onRowClick={row => onSelectAdditionalData(row, true)}
                allowHover={true}
                initialSearch={{ colField: 'adf.designation', isOrderAsc: true }}
            />
            <PaginationWithInfo
                itemName={t('additional_data.title')}
                currentPage={additionalDataPage?.currentPage || 1}
                pageItems={additionalDataPage?.items.length || 0}
                totalItems={additionalDataPage?.totalItems || 0}
                onChange={page => setCriteria({ ...criteria, page })}
            />

            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('common.messages.remove_record')} />
        </div>
    );
};

export default AdditionalDataList;
